import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { OfflineIndicator } from './components/OfflineIndicator';
import './App.css';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // First try with cookies
      let response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      
      // If cookies don't work, try with localStorage token
      if (!response.ok) {
        const token = localStorage.getItem('token');
        if (token) {
          response = await fetch(`${API}/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
      }
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    setUser(userData);
    if (token) localStorage.setItem('token', token);
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/auth/logout`, { 
        method: 'POST', 
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Lazy load pages
const Landing = React.lazy(() => import('./pages/Landing'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const ChildProfile = React.lazy(() => import('./pages/ChildProfile'));
const AddChild = React.lazy(() => import('./pages/AddChild'));
const Measurements = React.lazy(() => import('./pages/Measurements'));
const Milestones = React.lazy(() => import('./pages/Milestones'));
const Timeline = React.lazy(() => import('./pages/Timeline'));
const Tips = React.lazy(() => import('./pages/Tips'));
const Photos = React.lazy(() => import('./pages/Photos'));
const Badges = React.lazy(() => import('./pages/Badges'));
const Premium = React.lazy(() => import('./pages/Premium'));
const PremiumSuccess = React.lazy(() => import('./pages/PremiumSuccess'));
const Settings = React.lazy(() => import('./pages/Settings'));
const PercentileCharts = React.lazy(() => import('./pages/PercentileCharts'));
const Reminders = React.lazy(() => import('./pages/Reminders'));
const SleepTracker = React.lazy(() => import('./pages/SleepTracker'));
const FeedingTracker = React.lazy(() => import('./pages/FeedingTracker'));
const DiaperTracker = React.lazy(() => import('./pages/DiaperTracker'));
const GrowthLeaps = React.lazy(() => import('./pages/GrowthLeaps'));
const Community = React.lazy(() => import('./pages/Community'));
const PhotosSelector = React.lazy(() => import('./pages/PhotosSelector'));
const MeasurementsSelector = React.lazy(() => import('./pages/MeasurementsSelector'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Help = React.lazy(() => import('./pages/Help'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const Vaccines = React.lazy(() => import('./pages/Vaccines'));
const BirthData = React.lazy(() => import('./pages/BirthData'));
const Appointments = React.lazy(() => import('./pages/Appointments'));
const Allergies = React.lazy(() => import('./pages/Allergies'));
const AblactationGuide = React.lazy(() => import('./pages/AblactationGuide'));
const ExportPDF = React.lazy(() => import('./pages/ExportPDF'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const AdminAnalytics = React.lazy(() => import('./pages/AdminAnalytics'));
const Memories = React.lazy(() => import('./pages/Memories'));
const SiblingComparison = React.lazy(() => import('./pages/SiblingComparison'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const Caregivers = React.lazy(() => import('./pages/Caregivers'));
const BabyDiary = React.lazy(() => import('./pages/BabyDiary'));
const GrowthPrediction = React.lazy(() => import('./pages/GrowthPrediction'));
const HealthIntegrations = React.lazy(() => import('./pages/HealthIntegrations'));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-500 animate-pulse">Cargando...</p>
    </div>
  </div>
);

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </React.Suspense>
    );
  }

  return (
    <React.Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/child/:childId" element={<ProtectedRoute><ChildProfile /></ProtectedRoute>} />
        <Route path="/add-child" element={<ProtectedRoute><AddChild /></ProtectedRoute>} />
        <Route path="/measurements/:childId" element={<ProtectedRoute><Measurements /></ProtectedRoute>} />
        <Route path="/measurements" element={<ProtectedRoute><MeasurementsSelector /></ProtectedRoute>} />
        <Route path="/percentiles/:childId" element={<ProtectedRoute><PercentileCharts /></ProtectedRoute>} />
        <Route path="/milestones/:childId" element={<ProtectedRoute><Milestones /></ProtectedRoute>} />
        <Route path="/timeline/:childId" element={<ProtectedRoute><Timeline /></ProtectedRoute>} />
        <Route path="/tips/:childId" element={<ProtectedRoute><Tips /></ProtectedRoute>} />
        <Route path="/photos/:childId" element={<ProtectedRoute><Photos /></ProtectedRoute>} />
        <Route path="/photos" element={<ProtectedRoute><PhotosSelector /></ProtectedRoute>} />
        <Route path="/badges" element={<ProtectedRoute><Badges /></ProtectedRoute>} />
        <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
        <Route path="/premium" element={<ProtectedRoute><Premium /></ProtectedRoute>} />
        <Route path="/premium/success" element={<ProtectedRoute><PremiumSuccess /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        
        {/* Tracking features */}
        <Route path="/sleep/:childId" element={<ProtectedRoute><SleepTracker /></ProtectedRoute>} />
        <Route path="/feeding/:childId" element={<ProtectedRoute><FeedingTracker /></ProtectedRoute>} />
        <Route path="/diapers/:childId" element={<ProtectedRoute><DiaperTracker /></ProtectedRoute>} />
        <Route path="/leaps/:childId" element={<ProtectedRoute><GrowthLeaps /></ProtectedRoute>} />
        <Route path="/vaccines/:childId" element={<ProtectedRoute><Vaccines /></ProtectedRoute>} />
        <Route path="/birth-data/:childId" element={<ProtectedRoute><BirthData /></ProtectedRoute>} />
        <Route path="/appointments/:childId" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
        <Route path="/allergies/:childId" element={<ProtectedRoute><Allergies /></ProtectedRoute>} />
        <Route path="/ablactation/:childId" element={<ProtectedRoute><AblactationGuide /></ProtectedRoute>} />
        <Route path="/export/:childId" element={<ProtectedRoute><ExportPDF /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/memories/:childId" element={<ProtectedRoute><Memories /></ProtectedRoute>} />
        <Route path="/siblings/:childId" element={<ProtectedRoute><SiblingComparison /></ProtectedRoute>} />
        <Route path="/caregivers" element={<ProtectedRoute><Caregivers /></ProtectedRoute>} />
        <Route path="/diary/:childId" element={<ProtectedRoute><BabyDiary /></ProtectedRoute>} />
        <Route path="/growth-prediction/:childId" element={<ProtectedRoute><GrowthPrediction /></ProtectedRoute>} />
        <Route path="/health-integrations" element={<ProtectedRoute><HealthIntegrations /></ProtectedRoute>} />
        <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <OfflineIndicator />
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
